/**
 * FERP Portal - Operational Risk View (DRR-138)
 *
 * LIFE-CRITICAL: Projects ViolationCheck events from the Event primitive.
 * No separate registry — Risk is a pure read projection over ComplianceEvents
 * written by the task creation ViolationCheck path.
 *
 * Perception-safe (DRR-114): no "deficiency" / "missed care" language.
 * Shows policy conflicts that blocked or advised against task creation.
 *
 * Coverage Gaps panel: task groups with no MANDATED_BY edge (orphan nodes).
 * Per DRR-110: every task group MUST have a regulatory anchor.
 *
 * Scope: task:read (violations) + compliance:read (coverage gaps)
 */

import { useMemo, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  Group,
  SimpleGrid,
  Loader,
  Button,
  SegmentedControl,
  Divider,
  Tooltip,
} from '@mantine/core';
import {
  IconShieldExclamation,
  IconAlertTriangle,
  IconInfoCircle,
  IconRefresh,
  IconShieldCheck,
  IconBan,
  IconNetwork,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListEventsQuery,
  useOrphanTaskGroupsQuery,
  GqlEventCategory,
  GqlEventType,
  GqlEventSeverity,
} from '@/generated/graphql';

// ─── Perception-safe labels (DRR-114) ────────────────────────────────────────

const EVENT_NAME_LABEL: Record<string, string> = {
  task_creation_blocked_by_policy: 'Policy Conflict — Task Blocked',
  task_creation_warning_by_policy: 'Policy Advisory — Proceed with Caution',
  task_creation_deferred_by_policy: 'Policy Review — Deferred',
  violation_check_unavailable: 'Policy Check Unavailable',
};

function eventLabel(eventName: string): string {
  return EVENT_NAME_LABEL[eventName] ?? eventName;
}

const SEVERITY_CONFIG: Record<
  string,
  { color: string; label: string; icon: React.ReactNode }
> = {
  [GqlEventSeverity.CRITICAL]: {
    color: 'red',
    label: 'Blocked',
    icon: <IconBan size={14} />,
  },
  [GqlEventSeverity.WARNING]: {
    color: 'yellow',
    label: 'Advisory',
    icon: <IconAlertTriangle size={14} />,
  },
};

function severityConfig(sev: string) {
  return (
    SEVERITY_CONFIG[sev] ?? {
      color: 'gray',
      label: sev,
      icon: <IconInfoCircle size={14} />,
    }
  );
}

// ─── Category display helpers ─────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  adl: 'ADL',
  medication: 'Medication',
  clinical: 'Clinical',
  nutrition: 'Nutrition',
  social: 'Social',
  safety: 'Safety',
};

// ─── Component ───────────────────────────────────────────────────────────────

type SeverityFilter = 'ALL' | 'CRITICAL' | 'WARNING';

export function RiskListPage() {
  const tenantId = useTenantId();
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('ALL');

  const { data, loading, error, refetch } = useListEventsQuery({
    variables: {
      tenantId: tenantId ?? '',
      filter: {
        category: GqlEventCategory.COMPLIANCE,
        eventType: GqlEventType.COMPLIANCE_EVENT,
        ...(severityFilter !== 'ALL' && {
          severity: severityFilter as GqlEventSeverity,
        }),
      },
      limit: 100,
    },
    skip: !tenantId,
  });

  const {
    data: orphanData,
    loading: orphanLoading,
    refetch: refetchOrphans,
  } = useOrphanTaskGroupsQuery();

  const events = useMemo(() => data?.listEvents?.items ?? [], [data]);
  const orphans = useMemo(() => orphanData?.orphanTaskGroups ?? [], [orphanData]);

  const blockedCount = useMemo(
    () => events.filter((e) => e.severity === GqlEventSeverity.CRITICAL).length,
    [events],
  );

  const advisoryCount = useMemo(
    () => events.filter((e) => e.severity !== GqlEventSeverity.CRITICAL).length,
    [events],
  );

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view operational risk events.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Risk Events">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Operational Risk</Title>
            <Text c="dimmed" size="sm">
              Policy conflicts recorded during task creation — projected from
              compliance events
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => { void refetch(); void refetchOrphans(); }}
            loading={loading || orphanLoading}
          >
            Refresh
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        ) : (
          <>
            {/* Summary Cards */}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="blue" size="lg">
                    <IconShieldExclamation size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>{events.length}</Text>
                    <Text size="xs" c="dimmed">Total Policy Events</Text>
                  </div>
                </Group>
              </Card>

              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="red" size="lg">
                    <IconBan size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>{blockedCount}</Text>
                    <Text size="xs" c="dimmed">Tasks Blocked</Text>
                  </div>
                </Group>
              </Card>

              <Card withBorder p="md">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="yellow" size="lg">
                    <IconAlertTriangle size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xl" fw={700}>{advisoryCount}</Text>
                    <Text size="xs" c="dimmed">Advisory / Deferred</Text>
                  </div>
                </Group>
              </Card>

              <Tooltip
                label="Task groups with no MANDATED_BY edge. Per DRR-110, every task group must have a regulatory anchor."
                multiline
                w={260}
              >
                <Card withBorder p="md" style={{ cursor: 'help' }}>
                  <Group gap="xs">
                    <ThemeIcon
                      variant="light"
                      color={orphans.length > 0 ? 'orange' : 'green'}
                      size="lg"
                    >
                      <IconNetwork size={20} />
                    </ThemeIcon>
                    <div>
                      <Text size="xl" fw={700}>{orphanLoading ? '…' : orphans.length}</Text>
                      <Text size="xs" c="dimmed">Coverage Gaps</Text>
                    </div>
                  </Group>
                </Card>
              </Tooltip>
            </SimpleGrid>

            {/* Severity Filter */}
            <Group>
              <SegmentedControl
                value={severityFilter}
                onChange={(v) => setSeverityFilter(v as SeverityFilter)}
                data={[
                  { label: 'All', value: 'ALL' },
                  { label: 'Blocked', value: 'CRITICAL' },
                  { label: 'Advisory', value: 'WARNING' },
                ]}
              />
            </Group>

            {/* Events Table */}
            {events.length === 0 ? (
              <Alert
                icon={<IconShieldCheck size={16} />}
                color="green"
                title="No Policy Conflicts"
              >
                No ViolationCheck events recorded for the selected filter.
                All task creation paths are operating within policy bounds.
              </Alert>
            ) : (
              <Card withBorder p="md">
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Policy Event</Table.Th>
                      <Table.Th>Outcome</Table.Th>
                      <Table.Th>Entity Type</Table.Th>
                      <Table.Th>Recorded At</Table.Th>
                      <Table.Th>Event ID</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {events.map((ev) => {
                      const sev = severityConfig(ev.severity);
                      const recordedAt = new Date(ev.performedAt).toLocaleString();

                      return (
                        <Table.Tr
                          key={ev.id}
                          style={{
                            borderLeft:
                              ev.severity === GqlEventSeverity.CRITICAL
                                ? '3px solid var(--mantine-color-red-6)'
                                : ev.severity === GqlEventSeverity.WARNING
                                ? '3px solid var(--mantine-color-yellow-6)'
                                : undefined,
                          }}
                        >
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {eventLabel(ev.eventName)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={sev.color}
                              variant="light"
                              size="sm"
                              leftSection={sev.icon}
                            >
                              {sev.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">{ev.entityType}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{recordedAt}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed" ff="monospace">
                              {ev.id.slice(0, 16)}…
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Card>
            )}

            {/* Coverage Gaps panel (DRR-138 / DRR-110) */}
            <Divider />

            <div>
              <Title order={3}>Coverage Gaps</Title>
              <Text c="dimmed" size="sm">
                Task groups with no regulatory anchor (no MANDATED_BY edge).
                Per DRR-110, each must link to at least one RuleSet via{' '}
                <Text span ff="monospace" size="xs">edges: [{'{'}type: MANDATED_BY, target: rs-X-Y-Z{'}'}]</Text>.
              </Text>
            </div>

            {orphanLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : orphans.length === 0 ? (
              <Alert
                icon={<IconShieldCheck size={16} />}
                color="green"
                title="Full Regulatory Coverage"
              >
                All task groups have at least one MANDATED_BY edge. No coverage gaps detected.
              </Alert>
            ) : (
              <Card withBorder p="md">
                <Stack gap="xs" mb="xs">
                  <Alert
                    icon={<IconAlertTriangle size={14} />}
                    color="orange"
                    variant="light"
                  >
                    {orphans.length} task group{orphans.length !== 1 ? 's' : ''} lack a regulatory anchor.
                    Add <Text span ff="monospace" size="xs">edges: [{'{'}type: MANDATED_BY, target: rs-X-Y-Z{'}'}]</Text> to each task group configuration.
                  </Alert>
                </Stack>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Task Group</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Subgraph / Setting</Table.Th>
                      <Table.Th>Life Critical</Table.Th>
                      <Table.Th>Config ID</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {orphans.map((tg) => (
                      <Table.Tr
                        key={tg.id}
                        style={{
                          borderLeft: tg.isLifeCritical
                            ? '3px solid var(--mantine-color-red-6)'
                            : '3px solid var(--mantine-color-orange-6)',
                        }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>{tg.name}</Text>
                          {tg.description && (
                            <Text size="xs" c="dimmed">{tg.description}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color="gray" size="sm">
                            {CATEGORY_LABEL[tg.category] ?? tg.category}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{tg.subgraph}</Text>
                          <Text size="xs" c="dimmed">{tg.setting}</Text>
                        </Table.Td>
                        <Table.Td>
                          {tg.isLifeCritical ? (
                            <Badge color="red" variant="filled" size="sm">
                              Life Critical
                            </Badge>
                          ) : (
                            <Text size="xs" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" ff="monospace">
                            {tg.configId}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

export default RiskListPage;
